import { fetchApi, fetchApiJson } from '@inmagik/react-crud'
import { PartialWithId } from '@inmagik/react-crud/dist/types'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { FormikHelpers } from 'formik'
import { omit, orderBy } from 'lodash'
import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { useAuth } from '../../auth'
import { FiltriBilance } from '../../components/FiltriBilance'
import FormCliente from '../../components/FormCliente'
import MaterialIcon from '../../components/MaterialIcon'
import ModalConfirm from '../../components/ModalConfirm'
import ModalForm from '../../components/ModalForm'
import Paginator from '../../components/Paginator'
import TabMenu from '../../components/TabMenu'
import { API_URL, USER_ROLES } from '../../constants'
import {
  useBilanceList,
  useBilancia,
  useCreateBilancia,
  usePartialUpdateBilancia,
  useUpdateBilancia,
} from '../../hooks/bilance'
import { useCliente, usePartialUpdateCliente, useUpdateCliente } from '../../hooks/clienti'
import { useQsFilters } from '../../hooks/filters'
import { useProvinceList } from '../../hooks/province'
import { useCreateSede, useSediList, useUpdateSede } from '../../hooks/sedi'
import { useCreateUtente, useUpdateUtente } from '../../hooks/utenti'
import { Bilancia, Cliente, Sede, User } from '../../types'
import { transformErrorForForm, useIsAmministratore, useIsSupervisore } from '../../utils/utils'
import BilanceTable from '../Bilance/BilanceTable'
import FormBilancia from '../Bilance/FormBilancia'
import ListGroupFilters from '../Bilance/ListGroupFilters'
import ModalFiltersBilancia from '../BilanciaDettaglio/ModalFiltersBilancia'
import ModalPianificazionePeriodica from '../Interventi/ModalPianificazionePeriodica'
import ModalPianificazioneTaratura from '../Interventi/ModalPianificazioneTaratura'
import FormUtenteCliente from '../Utenti/FormUtenteCliente'
import FormSede from './FormSede'
import ModalRichieste from './ModalRichieste'
import SediTable from './SediTable'
import UtentiTable from './UtentiTable'

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get('page') ?? 1),
  search: params.get('search') ?? '',
  ordering: params.get('ordering') ?? 'ragione_sociale',
  tipo_bilancia: params.get('tipo_bilancia') ?? '',
  sede: params.get('sede') ?? '',
  verif_periodica: params.get('verif_periodica') ?? '',
  rapporto_taratura: params.get('rapporto_taratura') ?? '',
  data_scadenza_periodica__gte: params.get('data_scadenza_periodica__gte') ?? '',
  data_scadenza_periodica__lte: params.get('data_scadenza_periodica__lte') ?? '',
  data_scadenza_taratura__gte: params.get('data_scadenza_taratura__gte') ?? '',
  data_scadenza_taratura__lte: params.get('data_scadenza_taratura__lte') ?? '',
  con_richiesta_periodica: params.get('con_richiesta_periodica') ?? '',
  data_richiesta_periodica__gte: params.get('data_richiesta_periodica__gte') ?? '',
  data_richiesta_periodica__lte: params.get('data_richiesta_periodica__lte') ?? '',
  data_intervento_periodica_aperto__isnull: params.get('data_intervento_periodica_aperto__isnull') ?? '',
  data_intervento_periodica_aperto__gte: params.get('data_intervento_periodica_aperto__gte') ?? '',
  data_intervento_periodica_aperto__lte: params.get('data_intervento_periodica_aperto__lte') ?? '',
  data_intervento_taratura_aperto__isnull: params.get('data_intervento_taratura_aperto__isnull') ?? '',
  data_intervento_taratura_aperto__gte: params.get('data_intervento_taratura_aperto__gte') ?? '',
  data_intervento_taratura_aperto__lte: params.get('data_intervento_taratura_aperto__lte') ?? '',
  fuori_uso: params.get('fuori_uso') ?? '',
  dismessa: params.get('dismessa') ?? '',
})

const initialValuesUtente = {
  full_name: '',
  email: '',
  role: USER_ROLES.responsabile_cliente.value,
}

const initialValues = {
  ragione_sociale: '',
  ente_pubblico: false,
}

const initialValuesBilancia = {
  matricola: '',
  tipo_bilancia: '',
  tipo_bilancia_em: '',
  nome_produttore: '',
  rivenditore: '',
  sede: undefined,
  cert_approv: '',
}

const schemaUtente = yup.object().shape({
  full_name: yup.string().required().label('Nome e Cognome'),
  email: yup.string().email().required().label('Email'),
  role: yup.string().required().label('Ruolo'),
  // clienti: yup.array().of(yup.number()).min(1, 'Seleziona almeno un cliente').label('Aziende'),
})

const schemaBilancia = yup.object().shape({
  matricola: yup.string().required().label('Matricola'),
  tipo_bilancia: yup.string().required().label('Tipo bilancia'),
  nome_produttore: yup.string().required().label('Nome produttore'),
  sede: yup.number().required().label('Sede'),
  cert_approv: yup
    .string()
    .label('Provvedimento')
    .when('verif_periodica', (verif_periodica: any, schema: any) => {
      if (verif_periodica[0] !== 'false' && verif_periodica[0] !== false) {
        return schema.required()
      } else {
        return schema.notRequired()
      }
    }),
  anno_marcatura_ce: yup
    .string()
    .nullable()
    .matches(/^[0-9]{4}$/, 'Deve essere un anno a 4 cifre')
    .label('Anno marcatura CE'),
})

export default function ClienteDettaglio() {
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const { id } = useParams()
  const queryClient = useQueryClient()
  const cliente = useCliente(String(id))
  const {
    data: bilance,
    isLoading,
    refetch,
  } = useBilanceList({
    _cliente: String(id),
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
    tipo_bilancia: filters.tipo_bilancia,
    sede: filters.sede,
    verif_periodica: filters.verif_periodica,
    rapporto_taratura: filters.rapporto_taratura,
    data_scadenza_periodica__gte: filters.data_scadenza_periodica__gte,
    data_scadenza_periodica__lte: filters.data_scadenza_periodica__lte,
    data_scadenza_taratura__gte: filters.data_scadenza_taratura__gte,
    data_scadenza_taratura__lte: filters.data_scadenza_taratura__lte,
    con_richiesta_periodica: filters.con_richiesta_periodica,
    data_richiesta_periodica__gte: filters.data_richiesta_periodica__gte,
    data_richiesta_periodica__lte: filters.data_richiesta_periodica__lte,
    data_intervento_periodica_aperto__isnull: filters.data_intervento_periodica_aperto__isnull,
    data_intervento_periodica_aperto__gte: filters.data_intervento_periodica_aperto__gte,
    data_intervento_periodica_aperto__lte: filters.data_intervento_periodica_aperto__lte,
    data_intervento_taratura_aperto__isnull: filters.data_intervento_taratura_aperto__isnull,
    data_intervento_taratura_aperto__gte: filters.data_intervento_taratura_aperto__gte,
    data_intervento_taratura_aperto__lte: filters.data_intervento_taratura_aperto__lte,
    fuori_uso: filters.fuori_uso,
    dismessa: filters.dismessa,
  })
  const sedi = useSediList({
    cliente: String(id),
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
  })
  const [tab, setTab] = useState<'bilance' | 'sedi' | 'utenti'>('bilance')

  const initalValuesSede = {
    partita_iva: '',
    numero_rea: '',
    codice_fiscale: '',
    via: '',
    comune: undefined,
    provincia: undefined,
    toponimo: undefined,
    telefono: '',
    nazione: 'ITALIA',
    nome_referente: '',
    email_referente: '',
    sede_principale: false,
    cliente: Number(id),
    civico: '',
  } as PartialWithId<Sede>

  const [openFilters, setOpenFilters] = useState(false)

  const [modalFiltersState, modalFiltersActions] = useModalTrigger()

  const { mutateAsync: updatePartialBilancia } = usePartialUpdateBilancia()

  const [modalStateForm, modalActionsForm] = useModalTrigger<Cliente>()
  const [modalStateFormBilancia, modalActionsFormBilancia] = useModalTrigger<Bilancia>()
  const [modalStateSede, modalActionsSede] = useModalTrigger<Sede>()
  const [showDetailSede, setShowDetailSede] = useState(true)
  const [modalStateDelete, modalActionsDelete] = useModalTrigger<Cliente>()
  const [modalStateDeleteSede, modalActionsDeleteSede] = useModalTrigger<Sede>()
  const [modalStateDeleteBilancia, modalActionsDeleteBilancia] = useModalTrigger<Bilancia>()
  const [modalUpdateBilancia, modalActionsUpdateBilancia] = useModalTrigger<Bilancia>()
  const [modalStateAggiungiRichieste, modalActionsAggiungiRichieste] = useModalTrigger()
  const [modalStateUtenteForm, modalActionsUtenteForm] = useModalTrigger<User>()

  const [modalStatePianificaPeriodica, modalActionsPianificaPeriodica] = useModalTrigger()
  const [modalStatePianificaTaratura, modalActionsPianificaTaratura] = useModalTrigger()

  const { mutateAsync: updateCliente } = useUpdateCliente()

  const bilancia = useBilancia(
    String(modalUpdateBilancia.value?.id) || String(modalStateDeleteBilancia.value?.id),
    modalUpdateBilancia.isOpen || modalStateDeleteBilancia.isOpen
  )

  const saveCliente = useCallback(
    (operatore: PartialWithId<Cliente>, actions: FormikHelpers<Cliente>) => {
      return updateCliente(operatore)
        .then(() => {
          modalActionsForm.close()
          actions.resetForm()
          toast.success('Cliente aggiornato con successo', {
            position: 'bottom-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          })
          queryClient.invalidateQueries({
            queryKey: [`${API_URL}/bilance/sede`],
          })
        })
        .catch((e) => {
          actions.setErrors(transformErrorForForm(e))
        })
    },
    [modalActionsForm, modalStateForm, updateCliente]
  )

  const { mutateAsync: createUser } = useCreateUtente()
  const { mutateAsync: updateUser } = useUpdateUtente()

  const saveCustomer = useCallback(
    (utente: PartialWithId<User>, actions: FormikHelpers<User>) => {
      if (utente.id) {
        return updateUser(utente)
          .then(() => {
            modalActionsUtenteForm.close()
            actions.resetForm()
          })
          .catch((e) => {
            actions.setErrors(e)
          })
      } else {
        return createUser(utente)
          .then(() => {
            modalActionsUtenteForm.close()
            actions.resetForm()
          })
          .catch((e) => {
            actions.setErrors(e.data)
          })
      }
    },
    [modalActionsUtenteForm, modalStateUtenteForm, updateUser, createUser]
  )

  const { mutateAsync: createBilancia } = useCreateBilancia()
  const { mutateAsync: updateBilancia } = useUpdateBilancia()

  const saveBilancia = useCallback(
    (bilancia: PartialWithId<Bilancia>, actions: FormikHelpers<Bilancia>) => {
      if (bilancia.id) {
        // remove collaudo_xlsm if it's a string
        if (typeof bilancia.collaudo_xlsm === 'string') {
          delete bilancia.collaudo_xlsm
        }
        return updateBilancia(bilancia)
          .then(() => {
            modalActionsUpdateBilancia.close()
            actions.resetForm()
            toast.success('Bilancia aggiornata con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
          })
          .catch((e) => {
            actions.setErrors(transformErrorForForm(e))
          })
      } else {
        return createBilancia({
          ...bilancia,
          sede: bilancia?.sede,
        })
          .then(() => {
            modalActionsFormBilancia.close()
            actions.resetForm()
            toast.success('Bilancia creata con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
          })
          .catch((e) => {
            actions.setErrors(transformErrorForForm(e))
          })
      }
    },
    [modalActionsFormBilancia, createBilancia]
  )

  const { mutateAsync: createSede } = useCreateSede()
  const { mutateAsync: updateSede } = useUpdateSede()

  const saveSede = useCallback(
    (sede: PartialWithId<Sede>, actions: FormikHelpers<Sede>) => {
      if (sede.id) {
        if (typeof sede.dvr === 'string') {
          delete sede.dvr
        }
        return updateSede(sede)
          .then((result) => {
            modalActionsSede.close()
            setShowDetailSede(true)
            actions.resetForm()
            toast.success('Sede aggiornata con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
            queryClient.invalidateQueries({
              queryKey: [`${API_URL}/bilance/cliente`, result.cliente],
            })
          })
          .catch((e) => {
            actions.setErrors(transformErrorForForm(e))
          })
      } else {
        return createSede(sede)
          .then((result) => {
            modalActionsSede.close()
            actions.resetForm()
            toast.success('Sede creata con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
            queryClient.invalidateQueries({
              queryKey: [`${API_URL}/bilance/cliente`, String(result.cliente)],
            })
          })
          .catch((e) => {
            actions.setErrors(transformErrorForForm(e))
          })
      }
    },
    [createSede, modalActionsSede, updateSede]
  )

  const areFiltersActive = useMemo(() => {
    const { page, search, ordering, ...rest } = filters
    return Object.values(rest).some((v) => v !== '')
  }, [filters])

  const [isOpenEditDelete, setIsOpenEditDelete] = useState(false)

  const filtersListGroupRef = useRef<HTMLDivElement>(null)
  const editDeleteRef = useRef<HTMLDivElement>(null)

  const { mutateAsync: updatePartialCliente } = usePartialUpdateCliente()

  const { tokens } = useAuth()

  // click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersListGroupRef.current && !filtersListGroupRef.current.contains(event.target as Node)) {
        setOpenFilters(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [filtersListGroupRef])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editDeleteRef.current && !editDeleteRef.current.contains(event.target as Node)) {
        setIsOpenEditDelete(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [editDeleteRef])

  // when toggle modal sede set showDetailSede to true

  useEffect(() => {
    if (modalStateSede.isOpen && modalStateSede.value?.id) {
      setShowDetailSede(true)
    } else {
      setShowDetailSede(false)
    }
  }, [modalStateSede.isOpen])

  const [sedeFilter, setSedeFilter] = useState('')

  useEffect(() => {
    if (filters.sede) {
      fetchApiJson(`${API_URL}/bilance/sede/options/?id=${filters.sede}`, {
        headers: {
          Authorization: `Bearer ${tokens?.access}`,
        },
      }).then((data) => {
        setSedeFilter(data[0]?.label)
      })
    }
  }, [filters.sede])

  const navigate = useNavigate()

  const isAdmin = useIsAmministratore()
  const isSupervisore = useIsSupervisore()

  const province = useProvinceList()

  const provinceCodes = useMemo(() => {
    return province?.data?.map((p) => p.sigla)
  }, [province])

  const schemaSede = useMemo(() => {
    if (!provinceCodes) return null
    return yup.object().shape({
      toponimo: yup.string().required().label('Indirizzo'),
      via: yup.string().required().label('Indirizzo'),
      comune: yup.string().required().label('Comune'),
      provincia: yup.string().required().label('Provincia'),
      email_referente: yup.string().email().label('Email referente'),
      nazione: yup.string().required().label('Nazione'),
      codice_fiscale: yup
        .string()
        .when('$ente_pubblico', {
          is: (ente_pubblico: any) => ente_pubblico === true || ente_pubblico === 'true',
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        })
        .label('Codice fiscale'),
      numero_rea: yup.string().when('$ente_pubblico', {
        is: (ente_pubblico: any) => ente_pubblico === false || ente_pubblico === 'false',
        then: (schema) =>
          schema.required().test('valid-numero-rea', 'Numero REA non valido', function (value: any) {
            if (!value || value.length < 3) return false // Ensure there's a value and it's long enough
            const prefix = value.slice(0, 2) // Get the first two characters
            const numbers = value.slice(2) // Get the rest of the string
            return provinceCodes?.includes(prefix) && /^\d+$/.test(numbers) // Check if prefix is valid and the rest are digits
          }),
        otherwise: (schema) => schema.notRequired(),
      }),
    })
  }, [provinceCodes])

  const schema = useMemo(() => {
    if (!provinceCodes) return null

    return yup.object().shape({
      ragione_sociale: yup.string().required('Ragione sociale è obbligatoria').label('Ragione sociale'),
      sedi_write: yup.array().of(
        yup.object().shape({
          toponimo: yup.string().required('Toponimo è obbligatorio').label('Toponimo'),
          via: yup.string().required('Via è obbligatoria').label('Via'),
          civico: yup.string().required('Civico è obbligatorio').label('Civico'),
          email_referente: yup.string().email().label('Email referente'),
          comune: yup.string().required('Comune è obbligatorio').label('Comune'),
          provincia: yup.string().required('Provincia è obbligatoria').label('Provincia'),
          nazione: yup.string().required('Nazione è obbligatoria').label('Nazione'),
          codice_fiscale: yup
            .string()
            .when('$ente_pubblico', {
              is: (ente_pubblico: any) => {
                console.log('Context for codice_fiscale:', ente_pubblico)
                return ente_pubblico === true || ente_pubblico === 'true'
              },
              then: (schema) => schema.required('Codice fiscale è obbligatorio'),
              otherwise: (schema) => schema.notRequired().nullable(),
            })
            .label('Codice fiscale'),

          numero_rea: yup.string().when('$ente_pubblico', {
            is: (ente_pubblico: any) => {
              console.log('Context for numero_rea:', ente_pubblico)
              return ente_pubblico === false || ente_pubblico === 'false'
            },
            then: (schema) =>
              schema
                .required('Numero REA è obbligatorio')
                .label('Numero REA')
                .test('valid-numero-rea', 'Numero REA non valido', function (value: any) {
                  if (!value || value.length < 3) return false // Ensure there's a value and it's long enough
                  const prefix = value.slice(0, 2) // Get the first two characters
                  const numbers = value.slice(2) // Get the rest of the string
                  return provinceCodes?.includes(prefix) && /^\d+$/.test(numbers) // Check if prefix is valid and the rest are digits
                }),
            otherwise: (schema) => schema.notRequired(),
          }),
        })
      ),
    })
  }, [provinceCodes])

  const [isDownloadCertTaratura, setIsDownloadCertTaratura] = useState(false)
  const [isCreatingCertTaratura, setIsCreatingCertTaratura] = useState(true)
  const [isDownloadElencoBilancePdf, setIsDownloadElencoBilancePdf] = useState(false)

  const [numCertificatiTaratura, setNumCertificatiTaratura] = useState(0)
  useEffect(() => {
    const fetchNumeroCertificati = async () => {
      try {
        // Construct query parameters
        const queryParams = new URLSearchParams({
          tipo_bilancia: filters.tipo_bilancia,
          sede: filters.sede,
          verif_periodica: filters.verif_periodica,
          rapporto_taratura: filters.rapporto_taratura,
          data_scadenza_periodica__gte: filters.data_scadenza_periodica__gte,
          data_scadenza_periodica__lte: filters.data_scadenza_periodica__lte,
          data_scadenza_taratura__gte: filters.data_scadenza_taratura__gte,
          data_scadenza_taratura__lte: filters.data_scadenza_taratura__lte,
          con_richiesta_periodica: filters.con_richiesta_periodica,
          data_richiesta_periodica__gte: filters.data_richiesta_periodica__gte,
          data_richiesta_periodica__lte: filters.data_richiesta_periodica__lte,
          data_intervento_periodica_aperto__isnull: filters.data_intervento_periodica_aperto__isnull,
          data_intervento_periodica_aperto__gte: filters.data_intervento_periodica_aperto__gte,
          data_intervento_periodica_aperto__lte: filters.data_intervento_periodica_aperto__lte,
          data_intervento_taratura_aperto__isnull: filters.data_intervento_taratura_aperto__isnull,
          data_intervento_taratura_aperto__gte: filters.data_intervento_taratura_aperto__gte,
          data_intervento_taratura_aperto__lte: filters.data_intervento_taratura_aperto__lte,
          fuori_uso: filters.fuori_uso,
          dismessa: filters.dismessa,
          search: filters.search,
        })

        // Fetch data from API
        const response = await fetchApiJson(
          `${API_URL}/bilance/cliente/${id}/get-numero-certificati-taratura/?${queryParams}`,
          {
            headers: {
              Authorization: `Bearer ${tokens?.access}`,
            },
          }
        )

        const data = await response
        setNumCertificatiTaratura(data.count)
      } catch (error) {
        console.error('Error fetching numero certificati:', error)
      }
    }

    fetchNumeroCertificati()
  }, [id, filters, tokens])

  useEffect(() => {
    const interval = setInterval(() => {
      fetchApiJson(`${API_URL}/bilance/cliente/${id}/check-if-download-taratura-bulk/`, {
        headers: {
          Authorization: `Bearer ${tokens?.access}`,
        },
      }).then((data) => {
        if (data.status) {
          setIsCreatingCertTaratura(true)
        } else {
          setIsCreatingCertTaratura(false)
        }
      })
    }, 5000)
    return () => clearInterval(interval)
  }, [isDownloadCertTaratura, id, tokens])

  return (
    <div className="page">
      <div className="row mt-4">
        <div className="col-12">
          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                navigate(-1)
              }}
              className="p-sm no-link d-flex align-items-center pointer"
            >
              <span className="material-icons text-secondary">groups</span>
              <span className="ms-2">Lista clienti</span>
            </div>
            <span className="material-icons text-secondary">chevron_right</span>
            <div className="ms-2 p-sm bg-secondary-light p-2 text-secondary rounded">Dettaglio cliente</div>
          </div>
        </div>
        <div className="col-12 d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <div>
              <h1 className="title mt-2 me-4">{cliente.data?.ragione_sociale}</h1>
            </div>
            {/* button icon */}
            <div className="d-flex align-items-center position-relative">
              <Button
                onClick={() => {
                  setIsOpenEditDelete(!isOpenEditDelete)
                }}
                variant="light"
                className="text-secondary d-flex align-items-center"
                type="button"
              >
                <span className="material-icons">more_horiz</span>
              </Button>
              {isOpenEditDelete && (
                <div
                  className="list-group position-absolute"
                  ref={editDeleteRef}
                  style={{
                    zIndex: 1000,
                    width: '200px',
                    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
                    top: '40px',
                    borderRadius: '5px',
                  }}
                >
                  <button
                    onClick={() => {
                      modalActionsForm.open(cliente.data)
                    }}
                    className="list-group-item list-group-item-action d-flex align-items-center"
                  >
                    <MaterialIcon icon="edit" className="me-2" />
                    Modifica
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => {
                        modalActionsDelete.open(cliente.data)
                      }}
                      className="list-group-item list-group-item-action d-flex align-items-center text-danger"
                    >
                      <MaterialIcon icon="delete" className="me-2" />
                      Elimina
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="d-flex align-items-center">
            <div className="p-md">
              <span className="text-body-secondary">Numero rea:</span>
              <span className="ms-2">{cliente.data?.sede_principale?.numero_rea}</span>
            </div>
            <div className="p-md ms-4">
              <span className="text-body-secondary">Partita iva:</span>
              <span className="ms-2">{cliente.data?.sede_principale?.partita_iva}</span>
            </div>
            <div className="p-md ms-4">
              <span className="text-body-secondary">Numero sedi:</span>
              <span className="ms-2">{cliente.data?.numero_sedi}</span>
            </div>
          </div>
          <div className="d-flex align-items-center p-md mt-2">
            <span>{cliente.data?.sede_principale?.toponimo_data?.nome} </span>
            <span className="ms-1">
              {cliente.data?.sede_principale?.via}
              {', '}{' '}
            </span>
            <span className="ms-1">{cliente.data?.sede_principale?.civico}</span>
            <span className="ms-2">-</span>
            <span className="ms-2">
              {cliente.data?.sede_principale?.comune_data?.nome} {cliente.data?.sede_principale?.comune_data?.cap}
            </span>
            <span className="ms-2">{cliente.data?.sede_principale?.provincia_data?.sigla}</span>
          </div>
        </div>
        <div className="col-12 mt-1">
          <div className="d-flex align-items-center justify-content-between">
            <TabMenu
              tabs={[
                { label: 'Bilance', value: 'bilance' },
                { label: 'Sedi', value: 'sedi' },
                { label: 'Utenti', value: 'utenti' },
              ]}
              activeTab={tab}
              onTabChange={setTab}
            />
            <div className="d-flex align-items-center">
              {tab === 'bilance' && (
                <Button
                  disabled={isDownloadCertTaratura || numCertificatiTaratura === 0 || isCreatingCertTaratura}
                  onClick={async () => {
                    setIsDownloadCertTaratura(true)
                    const data = await fetchApi(
                      `${API_URL}/bilance/cliente/${id}/download-cert-taratura-bulk/?${new URLSearchParams({
                        tipo_bilancia: filters.tipo_bilancia,
                        sede: filters.sede,
                        verif_periodica: filters.verif_periodica,
                        rapporto_taratura: filters.rapporto_taratura,
                        data_scadenza_periodica__gte: filters.data_scadenza_periodica__gte,
                        data_scadenza_periodica__lte: filters.data_scadenza_periodica__lte,
                        data_scadenza_taratura__gte: filters.data_scadenza_taratura__gte,
                        data_scadenza_taratura__lte: filters.data_scadenza_taratura__lte,
                        con_richiesta_periodica: filters.con_richiesta_periodica,
                        data_richiesta_periodica__gte: filters.data_richiesta_periodica__gte,
                        data_richiesta_periodica__lte: filters.data_richiesta_periodica__lte,
                        data_intervento_periodica_aperto__isnull: filters.data_intervento_periodica_aperto__isnull,
                        data_intervento_periodica_aperto__gte: filters.data_intervento_periodica_aperto__gte,
                        data_intervento_periodica_aperto__lte: filters.data_intervento_periodica_aperto__lte,
                        data_intervento_taratura_aperto__isnull: filters.data_intervento_taratura_aperto__isnull,
                        data_intervento_taratura_aperto__gte: filters.data_intervento_taratura_aperto__gte,
                        data_intervento_taratura_aperto__lte: filters.data_intervento_taratura_aperto__lte,
                        fuori_uso: filters.fuori_uso,
                        dismessa: filters.dismessa,
                        search: filters.search,
                      })}`,
                      {
                        headers: {
                          Authorization: `Bearer ${tokens?.access}`,
                        },
                      }
                    )
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = 'rapporti-di-taratura.zip'
                    a.click()
                    setIsDownloadCertTaratura(false)
                  }}
                  variant="outline-secondary"
                  className="d-flex align-items-center bg-white me-2 text-secondary"
                  type="button"
                >
                  <MaterialIcon icon="download" className="me-2" />
                  Scarica Rapporti Taratura ({numCertificatiTaratura})
                  {isDownloadCertTaratura && (
                    <Spinner animation="border" variant="secondary" className="ms-4" size="sm" />
                  )}
                </Button>
              )}
              {tab === 'bilance' && (
                <Button
                  disabled={isDownloadElencoBilancePdf || bilance?.count === 0}
                  onClick={async () => {
                    setIsDownloadElencoBilancePdf(true)
                    const data = await fetchApi(
                      `${API_URL}/bilance/cliente/${id}/download-elenco-bilance-pdf/?${new URLSearchParams({
                        tipo_bilancia: filters.tipo_bilancia,
                        sede: filters.sede,
                        verif_periodica: filters.verif_periodica,
                        rapporto_taratura: filters.rapporto_taratura,
                        data_scadenza_periodica__gte: filters.data_scadenza_periodica__gte,
                        data_scadenza_periodica__lte: filters.data_scadenza_periodica__lte,
                        data_scadenza_taratura__gte: filters.data_scadenza_taratura__gte,
                        data_scadenza_taratura__lte: filters.data_scadenza_taratura__lte,
                        con_richiesta_periodica: filters.con_richiesta_periodica,
                        data_richiesta_periodica__gte: filters.data_richiesta_periodica__gte,
                        data_richiesta_periodica__lte: filters.data_richiesta_periodica__lte,
                        data_intervento_periodica_aperto__isnull: filters.data_intervento_periodica_aperto__isnull,
                        data_intervento_periodica_aperto__gte: filters.data_intervento_periodica_aperto__gte,
                        data_intervento_periodica_aperto__lte: filters.data_intervento_periodica_aperto__lte,
                        data_intervento_taratura_aperto__isnull: filters.data_intervento_taratura_aperto__isnull,
                        data_intervento_taratura_aperto__gte: filters.data_intervento_taratura_aperto__gte,
                        data_intervento_taratura_aperto__lte: filters.data_intervento_taratura_aperto__lte,
                        fuori_uso: filters.fuori_uso,
                        dismessa: filters.dismessa,
                        search: filters.search,
                      })}`,
                      {
                        headers: {
                          Authorization: `Bearer ${tokens?.access}`,
                        },
                      }
                    )
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    const cliente_str = cliente.data?.ragione_sociale.replace(/ /g, '_')
                    a.download = 'elenco-bilance' + cliente_str + '.pdf'
                    a.click()
                    setIsDownloadElencoBilancePdf(false)
                  }}
                  variant="outline-secondary"
                  className="d-flex align-items-center bg-white me-2 text-secondary"
                  type="button"
                >
                  <MaterialIcon icon="download" className="me-2" />
                  Scarica Bilance PDF
                  {isDownloadElencoBilancePdf && (
                    <Spinner animation="border" variant="secondary" className="ms-4" size="sm" />
                  )}
                </Button>
              )}
              {tab === 'bilance' && (isAdmin || isSupervisore) && (
                <>
                  <Button
                    onClick={() => {
                      modalActionsPianificaPeriodica.open()
                    }}
                    variant="white"
                    className="text-white d-flex align-items-center btn-outline-periodico"
                    type="button"
                  >
                    <MaterialIcon icon="edit_calendar" className="me-2" />
                    <span> Pianifica periodica</span>
                  </Button>
                  <Button
                    onClick={() => {
                      modalActionsPianificaTaratura.open()
                    }}
                    variant="white"
                    className="text-white d-flex align-items-center ms-2 btn-outline-taratura me-2"
                    type="button"
                  >
                    <MaterialIcon icon="edit_calendar" className="me-2" />
                    <span> Pianifica taratura</span>
                  </Button>
                </>
              )}
              {tab === 'bilance' && (isAdmin || isSupervisore) && (
                <Button
                  onClick={() => {
                    modalActionsAggiungiRichieste.open()
                  }}
                  variant="outline-secondary"
                  className="d-flex align-items-center bg-white me-2 text-secondary"
                  type="button"
                >
                  <span>Aggiungi richieste</span>
                  <MaterialIcon icon="add" className="ms-2" />
                </Button>
              )}
              {tab === 'bilance' && (
                <Button
                  onClick={() => {
                    modalActionsFormBilancia.open()
                  }}
                  variant="secondary"
                  className="text-white d-flex align-items-center"
                  type="button"
                >
                  <span>Crea bilancia</span>
                  <MaterialIcon icon="add" className="ms-2" />
                </Button>
              )}
              {tab === 'sedi' && (
                <Button
                  onClick={() => {
                    modalActionsSede.open()
                  }}
                  variant="secondary"
                  className="text-white d-flex align-items-center"
                  type="button"
                >
                  <span>Crea sede</span>
                  <MaterialIcon icon="add" className="ms-2" />
                </Button>
              )}
              {tab === 'utenti' && (
                <Button
                  onClick={() => {
                    modalActionsUtenteForm.open()
                  }}
                  variant="secondary"
                  className="text-white d-flex align-items-center"
                  type="button"
                >
                  <span>Crea utente</span>
                  <MaterialIcon icon="add" className="ms-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
        {tab === 'bilance' && (
          <div className="col-12 mt-2">
            <div className="bg-white p-2 border-bottom d-flex justify-content-between align-items-center w-100 rounded">
              <div className="ms-2 d-flex align-items-center">
                <input
                  type="text"
                  style={{ maxWidth: '500px' }}
                  className="form-control"
                  placeholder="Cerca per matricola..."
                  value={uiFilters.search}
                  onChange={(e) => setFilters({ search: e.target.value, page: 1 })}
                />
                <div className="ms-2 position-relative">
                  <Button
                    onClick={() => {
                      setOpenFilters(!openFilters)
                    }}
                    variant={openFilters ? 'secondary' : 'outline-secondary'}
                    className={classNames('d-flex align-items-center', {
                      'text-white': openFilters,
                    })}
                    type="button"
                  >
                    <span className="material-icons">filter_alt</span>
                    <span className="ms-2">Filtri</span>
                  </Button>
                  <div className="position-absolute" ref={filtersListGroupRef}>
                    {openFilters && (
                      <ListGroupFilters
                        filters={filters}
                        setFilters={setFilters}
                        modalFiltersActions={modalFiltersActions}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="me-2">
                Stai visualizzando {bilance?.count ?? 0} di {bilance?.full_count ?? 0} bilance
              </div>
            </div>
            {areFiltersActive && (
              <div className="bg-white p-2 border-bottom d-flex justify-content-between align-items-center w-100 rounded">
                <div className="d-flex align-items-center">
                  <FiltriBilance filters={filters} setFilters={setFilters} />
                </div>
                <div>
                  <Button
                    onClick={() => {
                      setFilters(initFilters(new URLSearchParams()))
                    }}
                    size="sm"
                    variant="danger"
                    className="d-flex align-items-center"
                    type="button"
                  >
                    <span className="material-icons me-2">filter_alt_off</span>
                    <span>Reset</span>
                  </Button>
                </div>
              </div>
            )}
            <BilanceTable
              bilance={bilance}
              isLoading={isLoading}
              setFilters={setFilters}
              uiFilters={uiFilters}
              hasFiltersActive={areFiltersActive}
              showSede
              modalActionsDelete={modalActionsDeleteBilancia}
              modalActionsForm={modalActionsUpdateBilancia}
            />
            <div className="position-relative">
              <Paginator
                count={bilance?.count ?? 0}
                currentPage={uiFilters.page}
                goToPage={(page) => setFilters({ page })}
              />
            </div>
          </div>
        )}
        {tab === 'sedi' && (
          <div className="col-12 mt-2">
            <div className="bg-white p-2 border-bottom d-flex justify-content-between align-items-center w-100 rounded">
              <div className="ms-2">
                <input
                  type="text"
                  style={{ maxWidth: '500px' }}
                  className="form-control"
                  placeholder="Cerca per sede..."
                  value={uiFilters.search}
                  onChange={(e) => setFilters({ search: e.target.value, page: 1 })}
                />
              </div>
              <div className="me-2">
                Stai visualizzando {sedi?.data?.count ?? 0} di {sedi?.data?.full_count ?? 0} sedi
              </div>
            </div>
            <SediTable
              sedi={sedi}
              setFilters={setFilters}
              uiFilters={uiFilters}
              setModal={modalActionsSede.open}
              modalActionsDelete={modalActionsDeleteSede}
            />
            <div className="position-relative">
              <Paginator
                count={sedi?.data?.count ?? 0}
                currentPage={uiFilters.page}
                goToPage={(page) => setFilters({ page })}
              />
            </div>
          </div>
        )}
        {tab === 'utenti' && (
          <div className="col-12 mt-2">
            <UtentiTable clienteFilter={Number(id)} />
          </div>
        )}
      </div>
      {modalStateForm.isOpen && (
        <ModalForm<Cliente>
          title={'Modifica cliente'}
          isOpen={modalStateForm.isOpen}
          toggle={modalActionsForm.toggle}
          initialValues={
            modalStateForm.value
              ? {
                  ...omit(modalStateForm.value, 'sedi_data'),
                  sedi_write: orderBy(modalStateForm.value.sedi_data, 'sede_principale', 'desc'),
                }
              : initialValues
          }
          save={saveCliente}
          schema={schema}
        >
          <FormCliente />
        </ModalForm>
      )}
      {modalStateFormBilancia.isOpen && (
        <ModalForm<Bilancia>
          title={'Crea bilancia'}
          isOpen={modalStateFormBilancia.isOpen}
          toggle={modalActionsFormBilancia.toggle}
          initialValues={
            modalStateFormBilancia.value ?? {
              ...initialValuesBilancia,
              cliente_pk: Number(id),
            }
          }
          save={saveBilancia}
          schema={schemaBilancia}
        >
          <FormBilancia />
        </ModalForm>
      )}
      {modalUpdateBilancia.isOpen && (
        <ModalForm<Bilancia>
          title={'Modifica bilancia'}
          isOpen={modalUpdateBilancia.isOpen}
          toggle={modalActionsUpdateBilancia.toggle}
          initialValues={bilancia.data ?? initialValuesBilancia}
          save={saveBilancia}
          schema={schemaBilancia}
        >
          <FormBilancia />
        </ModalForm>
      )}
      {modalStateSede.isOpen && (
        <ModalForm<Sede>
          title={modalStateSede.value ? 'Modifica sede' : 'Crea sede'}
          isOpen={modalStateSede.isOpen}
          toggle={modalActionsSede.toggle}
          initialValues={modalStateSede.value ?? initalValuesSede}
          save={saveSede}
          buttonExtraAction={() => {
            setShowDetailSede(!showDetailSede)
          }}
          showSave={!showDetailSede}
          schema={schemaSede}
          labelSave={showDetailSede ? 'Modifica' : 'Salva'}
        >
          <FormSede cliente={cliente.data} setShowDetailSede={setShowDetailSede} showDetailSede={showDetailSede} />
        </ModalForm>
      )}
      {modalFiltersState.isOpen && (
        <ModalFiltersBilancia
          isOpen={modalFiltersState.isOpen}
          toggle={() => {
            modalFiltersActions.close()
            setOpenFilters(false)
          }}
          setFilters={setFilters}
          filters={filters}
          cliente={cliente}
        />
      )}
      {modalStateDelete.isOpen && (
        <ModalConfirm
          isOpen={modalStateDelete.isOpen}
          toggle={modalActionsDelete.toggle}
          title="Elimina cliente"
          confirmText="Elimina"
          body={'Sei sicuro di voler eliminare il cliente ' + cliente.data?.ragione_sociale + '?'}
          action={async () => {
            await updatePartialCliente({
              ...cliente.data,
              cancellato: true,
            })
            modalActionsDelete.close()
            toast.success('Cliente eliminato con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
            navigate(-1)
          }}
        />
      )}
      {modalStateDeleteSede.isOpen && (
        <ModalConfirm
          isOpen={modalStateDeleteSede.isOpen}
          toggle={modalActionsDeleteSede.toggle}
          title="Elimina sede"
          confirmText="Elimina"
          body={
            'Sei sicuro di voler eliminare la sede ' +
            modalStateDeleteSede.value?.toponimo_data?.nome +
            ' ' +
            modalStateDeleteSede.value?.via +
            ', ' +
            modalStateDeleteSede.value?.civico +
            ', ' +
            modalStateDeleteSede.value?.comune_data?.nome +
            ', ' +
            modalStateDeleteSede.value?.provincia_data?.sigla +
            '?'
          }
          action={async () => {
            await updateSede({
              ...modalStateDeleteSede.value,
              cancellato: true,
            })
            modalActionsDeleteSede.close()
            toast.success('Sede eliminata con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
          }}
        />
      )}
      {modalStateDeleteBilancia.isOpen && (
        <ModalConfirm
          isOpen={modalStateDeleteBilancia.isOpen}
          toggle={modalActionsDeleteBilancia.toggle}
          title="Elimina bilancia"
          confirmText="Elimina"
          body={
            'Sei sicuro di voler eliminare la bilancia con matricola' + modalStateDeleteBilancia.value?.matricola + '?'
          }
          action={async () => {
            await updatePartialBilancia({
              ...modalStateDeleteBilancia.value,
              cancellato: true,
            })
            modalActionsDeleteBilancia.close()
            toast.success('Bilancia eliminata con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
          }}
        />
      )}
      {modalStateAggiungiRichieste.isOpen && (
        <ModalRichieste
          cliente={cliente.data}
          isOpen={modalStateAggiungiRichieste.isOpen}
          save={async (values: any) => {
            await fetchApi(`${API_URL}/bilance/intervento/aggiungi-richieste-periodiche/`, {
              method: 'POST',
              body: JSON.stringify(values),
              headers: {
                Authorization: `Bearer ${tokens?.access}`,
                'Content-Type': 'application/json',
              },
            }).then(() => {
              refetch()
              modalActionsAggiungiRichieste.close()
            })
          }}
          toggle={modalActionsAggiungiRichieste.toggle}
        />
      )}
      {modalStatePianificaPeriodica.isOpen && (
        <ModalPianificazionePeriodica
          isOpen={modalStatePianificaPeriodica.isOpen}
          cliente_id={Number(id)}
          toggle={modalActionsPianificaPeriodica.toggle}
          save={async (values: any) => {
            await fetchApi(`${API_URL}/bilance/intervento/pianifica/`, {
              method: 'POST',
              body: JSON.stringify(values),
              headers: {
                Authorization: `Bearer ${tokens?.access}`,
                'Content-Type': 'application/json',
              },
            }).then(() => {
              refetch()
              modalActionsPianificaPeriodica.close()
            })
          }}
        />
      )}
      {modalStatePianificaTaratura.isOpen && (
        <ModalPianificazioneTaratura
          isOpen={modalStatePianificaTaratura.isOpen}
          cliente_id={Number(id)}
          toggle={modalActionsPianificaTaratura.toggle}
          save={async (values: any) => {
            await fetchApi(`${API_URL}/bilance/intervento/pianifica-tarature/`, {
              method: 'POST',
              body: JSON.stringify(values),
              headers: {
                Authorization: `Bearer ${tokens?.access}`,
                'Content-Type': 'application/json',
              },
            }).then(() => {
              refetch()
              modalActionsPianificaTaratura.close()
            })
          }}
        />
      )}
      <ModalForm<User>
        title={modalStateUtenteForm.value ? 'Modifica utente' : 'Crea utente'}
        isOpen={modalStateUtenteForm.isOpen}
        toggle={modalActionsUtenteForm.toggle}
        initialValues={
          modalStateUtenteForm.value ?? {
            ...initialValuesUtente,
            clienti: [Number(id)],
          }
        }
        save={saveCustomer}
        schema={schemaUtente}
      >
        <FormUtenteCliente />
      </ModalForm>
    </div>
  )
}
